import axios, { AxiosError, AxiosRequestConfig } from 'axios'
import _ from 'lodash'

import { assignment } from '@/lib/utils/assignment'
import { transformApiErrorsToString, transformApiFormErrors } from '@/lib/utils/transformEntries'

import app from '@/app'
import { useErrorService } from '@/composables/useErrorService'
import { useAppStore } from '@/stores/app'
import { usePunchoutStore } from '@/stores/punchout'
import { useSessionStore } from '@/stores/session'

const _debouncedEmitter = _.debounce((axiosError) => {
  app.config.globalProperties.$emitter.emit(
    'auth.notAuthorized',
    transformApiFormErrors(axiosError),
  )
}, 500)

class Transport {
  baseUrl: string

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl
  }

  /**
   * Get an absolute API URL
   * @param endpoint
   * @returns {string}
   */
  getApiUrl(endpoint: string) {
    return `${this.baseUrl}${endpoint}`
  }

  /**
   * Get HTTP headers for API request
   * @returns {{'Content-Type': string}}
   */
  getHeaders() {
    const headers = {
      'Content-Type': 'application/json',
    }
    return headers
  }

  /**
   * Perform a request.
   * @param method
   * @param endpoint
   * @param data
   * @param headers
   * @returns {Promise<any>}
   */
  async request(
    method: string,
    endpoint: string,
    data?: object | string | number | null,
    headers?: Record<string, string> | null,
    requestOptions?: AxiosRequestConfig | null,
    transportOptions?: Record<string, unknown> | null,
  ) {
    const appStore = useAppStore()
    const sessionStore = useSessionStore()

    const headersData = headers || {}
    const requestOptionsData = requestOptions || {}
    const transportOptionsData = transportOptions || {}
    const isFullInitializedOnRequestStart = appStore.isFullInitialized

    try {
      if (transportOptionsData.checkSession !== false) await sessionStore.checkSession()
      if (transportOptionsData.trackRequest !== false)
        await sessionStore.setLastRequest(new Date().toISOString())
      const uniqueHeaders = assignment(this.getHeaders(), headersData || {})
      const options: AxiosRequestConfig = {
        url: this.getApiUrl(endpoint),
        withCredentials: true,
        method,
        data,
        headers: uniqueHeaders,
        ...requestOptionsData,
      }
      const response = await axios(options)
      return response
    } catch (error) {
      const punchoutStore = usePunchoutStore()
      const errorService = useErrorService()

      const axiosError = error as AxiosError

      if (!isFullInitializedOnRequestStart) {
        errorService.decorateError('useAppInit', axiosError, {
          errorLevel: 'boot',
        })
        if (punchoutStore.isPunchoutSession) {
          const errorMessage = transformApiErrorsToString(axiosError) || 'Something went wrong'
          appStore.setCriticalViewData({
            templateName: 'punchout',
            data: errorMessage,
          })
        } else if (window.localStorage?.getItem('bioForceReloadOnError')) {
          window.localStorage.clear()
          window.location.reload()
        } else {
          if (window.localStorage) {
            window.localStorage.clear()
          }
        }
      } else if (axiosError && axiosError.response && axiosError.response.status === 401) {
        _debouncedEmitter(axiosError)
      }
      throw axiosError
    }
  }

  /**
   * POST request.
   * @param endpoint
   * @param data
   * @param headers
   * @returns {*|Promise<any>}
   */
  post(
    endpoint: string,
    data?: object | string | number | null,
    headers?: Record<string, string> | null,
    requestOptions?: AxiosRequestConfig | null,
    transportOptions?: Record<string, unknown> | null,
  ) {
    return this.request('post', endpoint, data, headers, requestOptions, transportOptions)
  }

  /**
   * DELETE request.
   * @param endpoint
   * @param data
   * @param headers
   * @returns {*|Promise<any>}
   */
  delete(
    endpoint: string,
    data?: object | string | number | null,
    headers?: Record<string, string> | null,
    requestOptions?: AxiosRequestConfig | null,
    transportOptions?: Record<string, unknown> | null,
  ) {
    return this.request('delete', endpoint, data, headers, requestOptions, transportOptions)
  }

  /**
   * GET request.
   * @param endpoint
   * @param data
   * @param headers
   * @returns {*|Promise<any>}
   */
  get(
    endpoint: string,
    data?: object | string | number | null,
    headers?: Record<string, string> | null,
    requestOptions?: AxiosRequestConfig | null,
    transportOptions?: Record<string, unknown> | null,
  ) {
    return this.request('get', endpoint, data, headers, requestOptions, transportOptions)
  }

  /**
   * PATCH request.
   * @param endpoint
   * @param data
   * @param headers
   * @returns {*|Promise<any>}
   */
  patch(
    endpoint: string,
    data?: object | string | number | null,
    headers?: Record<string, string> | null,
    requestOptions?: AxiosRequestConfig | null,
    transportOptions?: Record<string, unknown> | null,
  ) {
    return this.request('patch', endpoint, data, headers, requestOptions, transportOptions)
  }

  /**
   * PUT request.
   * @param endpoint
   * @param data
   * @param headers
   * @returns {*|Promise<any>}
   */
  put(
    endpoint: string,
    data?: object | string | number | null,
    headers?: Record<string, string> | null,
    requestOptions?: AxiosRequestConfig | null,
    transportOptions?: Record<string, unknown> | null,
  ) {
    return this.request('put', endpoint, data, headers, requestOptions, transportOptions)
  }
}

export default Transport
